import React, { useCallback, useEffect, useMemo, useState } from "react";
import Icons from "../icons";
import HelpButton from "./HelpDialog";
import { statusOf } from "../testStatus";
import { useTheme } from "../theme";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* Buckets any test-case status into the three we chart. */
function bucketOf(status) {
  if (status === "passed") return "passed";
  if (status === "failed") return "failed";
  return "pending"; // pending / running / anything else
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

/* ------------------------------------------------------------------ */
/*  KPI card                                                           */
/* ------------------------------------------------------------------ */
function KpiCard({
  icon: Glyph,
  label,
  value,
  sub,
  accent = "text-brand-700 bg-brand-50",
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-500">{label}</span>
        <span
          className={[
            "flex h-9 w-9 items-center justify-center rounded-lg",
            accent,
          ].join(" ")}
        >
          <Glyph className="h-5 w-5" />
        </span>
      </div>
      <div className="mt-3 text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Segmented status bar                                               */
/* ------------------------------------------------------------------ */
function StatusBar({ passed, failed, pending, total }) {
  const pct = (n) => (total > 0 ? (n / total) * 100 : 0);
  const segments = [
    { key: "passed", value: passed, cls: "bg-emerald-500" },
    { key: "failed", value: failed, cls: "bg-rose-500" },
    { key: "pending", value: pending, cls: "bg-slate-300" },
  ];
  return (
    <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
      {total === 0 ? (
        <div className="h-full w-full bg-slate-100" />
      ) : (
        segments.map((s) => (
          <div
            key={s.key}
            className={["h-full transition-all", s.cls].join(" ")}
            style={{ width: `${pct(s.value)}%` }}
          />
        ))
      )}
    </div>
  );
}

function LegendItem({ dotCls, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={["h-2.5 w-2.5 flex-none rounded-full", dotCls].join(" ")}
      />
      <span className="text-sm text-slate-600">{label}</span>
      <span className="ml-auto text-sm font-semibold text-slate-900">
        {value}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Dashboard                                                          */
/* ------------------------------------------------------------------ */
export default function Dashboard({
  boards,
  onOpenBoard,
  onViewAllBoards,
  onViewUsers,
}) {
  const { username } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userCount, setUserCount] = useState(null);
  const [boardStats, setBoardStats] = useState([]); // [{ ...board, total, passed, failed, pending }]
  const [recentCases, setRecentCases] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Users (best-effort) + per-board test cases, all in parallel.
      const usersPromise = fetch(`${API_BASE_URL}users`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);

      const boardPromise = Promise.all(
        boards.map(async (b) => {
          try {
            const res = await fetch(
              `${API_BASE_URL}testcases?board_id=${encodeURIComponent(b.id)}`,
            );
            const body = await res.json().catch(() => ({}));
            const cases = Array.isArray(body?.data) ? body.data : [];
            return { board: b, cases };
          } catch {
            return { board: b, cases: [] };
          }
        }),
      );

      const [usersBody, boardResults] = await Promise.all([
        usersPromise,
        boardPromise,
      ]);

      setUserCount(
        Array.isArray(usersBody?.data) ? usersBody.data.length : null,
      );

      const stats = boardResults.map(({ board, cases }) => {
        let passed = 0;
        let failed = 0;
        let pending = 0;
        cases.forEach((c) => {
          const bucket = bucketOf(c.status);
          if (bucket === "passed") passed += 1;
          else if (bucket === "failed") failed += 1;
          else pending += 1;
        });
        return { ...board, total: cases.length, passed, failed, pending };
      });
      setBoardStats(stats);

      // Flatten every case, tag with its board, sort newest first.
      const flat = [];
      boardResults.forEach(({ board, cases }) => {
        cases.forEach((c) =>
          flat.push({ ...c, boardName: board.name, boardId: board.id }),
        );
      });
      flat.sort(
        (a, b) =>
          new Date(b.createddate || 0).getTime() -
          new Date(a.createddate || 0).getTime(),
      );
      setRecentCases(flat.slice(0, 6));
    } catch (e) {
      setError(e.message || "Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  }, [boards]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(() => {
    const t = {
      boards: boardStats.length,
      cases: 0,
      passed: 0,
      failed: 0,
      pending: 0,
    };
    boardStats.forEach((b) => {
      t.cases += b.total;
      t.passed += b.passed;
      t.failed += b.failed;
      t.pending += b.pending;
    });
    t.passRate = t.cases > 0 ? Math.round((t.passed / t.cases) * 100) : 0;
    return t;
  }, [boardStats]);

  const busiestBoard = useMemo(() => {
    if (boardStats.length === 0) return null;
    return [...boardStats].sort((a, b) => b.total - a.total)[0];
  }, [boardStats]);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            {username ? `Welcome back, ${username}` : "Dashboard"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Analytics across your test suites and cases.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <Icons.Refresh
              className={["h-4 w-4", loading ? "animate-spin" : ""].join(" ")}
            />
            Refresh
          </button>
          <HelpButton screen="dashboard" />
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}

      {loading && boardStats.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">
          <Icons.Spinner className="mx-auto mb-2 h-5 w-5 animate-spin" />
          Loading analytics…
        </div>
      ) : (
        <div className="space-y-6">
          {/* KPI row */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              icon={Icons.Boards}
              label="Test Suites"
              value={totals.boards}
              sub={
                busiestBoard
                  ? `Busiest: ${busiestBoard.name}`
                  : "No test suites yet"
              }
            />
            <KpiCard
              icon={Icons.Grid}
              label="Test cases"
              value={totals.cases}
              sub={`${totals.pending} not run yet`}
              accent="text-indigo-700 bg-indigo-50"
            />
            <KpiCard
              icon={Icons.Check}
              label="Pass rate"
              value={`${totals.passRate}%`}
              sub={`${totals.passed} passed · ${totals.failed} failed`}
              accent="text-emerald-700 bg-emerald-50"
            />
            <KpiCard
              icon={Icons.Users}
              label="Registered users"
              value={userCount ?? "—"}
              sub={userCount != null ? "From users directory" : "Unavailable"}
              accent="text-amber-700 bg-amber-50"
            />
          </div>

          {/* Distribution + recent activity */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {/* Status distribution */}
            <div className="rounded-xl border border-slate-200 bg-card p-5 shadow-sm lg:col-span-1">
              <div className="mb-4 flex items-center gap-2">
                <Icons.Chart className="h-4 w-4 text-slate-400" />
                <h2 className="text-sm font-semibold text-slate-900">
                  Status distribution
                </h2>
              </div>
              <StatusBar
                passed={totals.passed}
                failed={totals.failed}
                pending={totals.pending}
                total={totals.cases}
              />
              <div className="mt-4 space-y-2">
                <LegendItem
                  dotCls="bg-emerald-500"
                  label="Passed"
                  value={totals.passed}
                />
                <LegendItem
                  dotCls="bg-rose-500"
                  label="Failed"
                  value={totals.failed}
                />
                <LegendItem
                  dotCls="bg-slate-300"
                  label="Not run"
                  value={totals.pending}
                />
              </div>
            </div>

            {/* Recent test cases */}
            <div className="rounded-xl border border-slate-200 bg-card p-5 shadow-sm lg:col-span-2">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icons.Activity className="h-4 w-4 text-slate-400" />
                  <h2 className="text-sm font-semibold text-slate-900">
                    Recent test cases
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={onViewAllBoards}
                  className="text-xs font-medium text-brand-700 hover:text-brand-800"
                >
                  View all test suites
                </button>
              </div>
              {recentCases.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-500">
                  No test cases yet.
                </p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {recentCases.map((c) => {
                    const s = statusOf(bucketOf(c.status));
                    return (
                      <li key={c.testcasekey}>
                        <button
                          type="button"
                          onClick={() => onOpenBoard?.(c.boardId)}
                          className="flex w-full items-center gap-3 py-2.5 text-left hover:bg-slate-50"
                        >
                          <span
                            className={[
                              "h-2 w-2 flex-none rounded-full",
                              s.dot,
                            ].join(" ")}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-slate-800">
                              {c.name}
                            </p>
                            <p className="truncate text-xs text-slate-400">
                              {c.boardName}
                            </p>
                          </div>
                          <span
                            className={[
                              "flex-none text-xs font-medium",
                              s.text,
                            ].join(" ")}
                          >
                            {s.label}
                          </span>
                          <span className="w-20 flex-none text-right text-xs text-slate-400">
                            {formatDate(c.createddate)}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* Boards overview */}
          <div className="rounded-xl border border-slate-200 bg-card shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div className="flex items-center gap-2">
                <Icons.Boards className="h-4 w-4 text-slate-400" />
                <h2 className="text-sm font-semibold text-slate-900">
                  Test Suites overview
                </h2>
              </div>
              <button
                type="button"
                onClick={onViewAllBoards}
                className="text-xs font-medium text-brand-700 hover:text-brand-800"
              >
                Open test suites
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead>
                  <tr className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3">Test Suite</th>
                    <th className="px-4 py-3">Tests</th>
                    <th className="px-4 py-3">Passed</th>
                    <th className="px-4 py-3">Failed</th>
                    <th className="px-4 py-3">Not run</th>
                    <th className="px-4 py-3 w-56">Pass rate</th>
                  </tr>
                </thead>
                <tbody>
                  {boardStats.map((b) => {
                    const rate =
                      b.total > 0 ? Math.round((b.passed / b.total) * 100) : 0;
                    return (
                      <tr
                        key={b.id}
                        onClick={() => onOpenBoard?.(b.id)}
                        className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                      >
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div
                              className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gradient-to-br ${b.accent} text-white`}
                            >
                              <Icons.Grid className="h-4 w-4" />
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium text-slate-900">
                                {b.name}
                              </div>
                              <div className="truncate text-xs text-slate-500">
                                {b.description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-600">
                          {b.total}
                        </td>
                        <td className="px-4 py-3 text-sm text-emerald-600">
                          {b.passed}
                        </td>
                        <td className="px-4 py-3 text-sm text-rose-600">
                          {b.failed || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {b.pending || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                              <div
                                className="h-full rounded-full bg-emerald-500"
                                style={{ width: `${rate}%` }}
                              />
                            </div>
                            <span className="w-9 flex-none text-right text-xs font-medium text-slate-500">
                              {rate}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
